// src/sections/Contact.js
import React, { useMemo, useState } from "react";
import "./Contact.css";

export default function Contact() {
  // ✅ set these
  const EMAIL = "riachipatrick1@gmail.com";
  const LINKEDIN = "https://www.linkedin.com/in/patrick-riachi-294547172/";

  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "Project / freelance",
    timeline: "",
    budget: "",
    callPref: "",
    message: "",
  });

  const [status, setStatus] = useState({ type: "idle", msg: "" }); // idle | ok | err
  const [copied, setCopied] = useState(false);

  const mailtoHref = useMemo(() => {
    const name = form.name.trim() || "New message";
    const subject = encodeURIComponent(`Call / inquiry — ${name} (${form.topic})`);

    const bodyLines = [
      form.message.trim(),
      "",
      "— Details —",
      `Topic: ${form.topic || "-"}`,
      `Timeline: ${form.timeline.trim() || "-"}`,
      `Budget/Range: ${form.budget.trim() || "-"}`,
      `Call preference: ${form.callPref.trim() || "-"}`,
      "",
      "— Contact —",
      form.name.trim() ? `Name: ${form.name.trim()}` : "",
      form.email.trim() ? `Email: ${form.email.trim()}` : "",
    ].filter(Boolean);

    const body = encodeURIComponent(bodyLines.join("\n"));
    return `mailto:${EMAIL}?subject=${subject}&body=${body}`;
  }, [EMAIL, form]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setStatus({ type: "ok", msg: "Email copied." });
      setTimeout(() => {
        setCopied(false);
        setStatus({ type: "idle", msg: "" });
      }, 1400);
    } catch {
      setStatus({ type: "err", msg: "Couldn’t copy. Tap the email to select it." });
      setTimeout(() => setStatus({ type: "idle", msg: "" }), 2400);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const n = form.name.trim();
    const em = form.email.trim();
    const m = form.message.trim();

    if (!n || !em || !m) {
      setStatus({ type: "err", msg: "Fill name, email, and message." });
      setTimeout(() => setStatus({ type: "idle", msg: "" }), 2400);
      return;
    }

    window.location.href = mailtoHref;
    setStatus({ type: "ok", msg: "Draft opened in your email client." });
    setTimeout(() => setStatus({ type: "idle", msg: "" }), 2400);
  };

  return (
    <section className="contact" id="contact" aria-label="Contact">
      <div className="contact__wrap">
        <div className="contact__header">
          <div className="contact__kicker">Contact</div>
          <h2 className="contact__title">Let’s schedule a call.</h2>
          <p className="contact__sub">
            Tell me what you’re trying to build (or solve) and I’ll come prepared with a plan,
            timeline, and next steps.
          </p>
        </div>

        <div className="contact__panel">
          {/* Modern top bar */}
          <div className="contactTop" aria-label="Quick actions">
            <button className="emailPill" type="button" onClick={copyEmail} title="Copy email">
              <span className="emailPill__icon" aria-hidden="true">
                {/* Mail icon */}
                <svg viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </span>

              <span className="emailPill__text">
                <span className="emailPill__label">Email</span>
                <span className="emailPill__value">{EMAIL}</span>
              </span>

              <span className={`emailPill__cta ${copied ? "isCopied" : ""}`}>
                {copied ? "Copied" : "Copy"}
              </span>
            </button>

            <a
              className="socialBtn"
              href={LINKEDIN}
              target="_blank"
              rel="noreferrer"
              aria-label="Open LinkedIn"
              title="LinkedIn"
            >
              <span className="socialBtn__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM.5 8h4V23h-4V8zm7 0h3.8v2.05h.05C12.06 8.84 13.84 7.5 16.3 7.5 20.35 7.5 23 10.1 23 15.1V23h-4v-7.05c0-2.62-.05-6-3.65-6-3.65 0-4.2 2.85-4.2 5.8V23h-4V8z" />
                </svg>
              </span>
              <span className="socialBtn__hint" aria-hidden="true">
                ↗
              </span>
            </a>

            <div className="microBadge" aria-label="Response time">
              <span className="microBadge__dot" aria-hidden="true" />
              <span className="microBadge__text">
                <span className="microBadge__label">Response</span>
                <span className="microBadge__value">24–48h</span>
              </span>
            </div>
          </div>

          {/* Form */}
          <form className="contact__form" onSubmit={onSubmit}>
            <div className="contact__grid">
              <div className="cField">
                <input
                  className="cField__control"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder=" "
                  autoComplete="name"
                />
                <span className="cField__label">Name</span>
              </div>

              <div className="cField">
                <input
                  className="cField__control"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder=" "
                  autoComplete="email"
                />
                <span className="cField__label">Email</span>
              </div>
            </div>

            <div className="contact__grid contact__grid--tri">
              <div className="cField">
                <select
                  className="cField__control cField__control--select"
                  name="topic"
                  value={form.topic}
                  onChange={onChange}
                  aria-label="Inquiry type"
                >
                  <option>Project / freelance</option>
                  <option>Full-time role</option>
                  <option>Consulting</option>
                  <option>Collaboration</option>
                  <option>Other</option>
                </select>
                <span className="cField__label cField__label--static">Reason</span>
              </div>

              <div className="cField">
                <input
                  className="cField__control"
                  name="timeline"
                  value={form.timeline}
                  onChange={onChange}
                  placeholder=" "
                />
                <span className="cField__label">Timeline (e.g., 2–4 weeks)</span>
              </div>

              <div className="cField">
                <input
                  className="cField__control"
                  name="budget"
                  value={form.budget}
                  onChange={onChange}
                  placeholder=" "
                />
                <span className="cField__label">Budget / range (optional)</span>
              </div>
            </div>

            <div className="cField">
              <input
                className="cField__control"
                name="callPref"
                value={form.callPref}
                onChange={onChange}
                placeholder=" "
              />
              <span className="cField__label">Preferred call time (timezone + a few slots)</span>
            </div>

            <div className="cField">
              <textarea
                className="cField__control cField__control--ta"
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder=" "
                rows={6}
              />
              <span className="cField__label">
                What do you need help with? (scope, links, success criteria)
              </span>
            </div>

            <div className="contact__actions">
              <button className="btn btn--primary" type="submit">
                Send message
              </button>

              <div className={`contact__status contact__status--${status.type}`} aria-live="polite">
                {status.msg || " "}
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
